/**
 * SettingsContext - manages application settings
 */
import { storageLogger } from '@annote/core/utils/debug';
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { Settings, DEFAULT_SETTINGS, SettingPath } from '@annote/settings';
import { ui, IconSize } from '@annote/core/styles';
import { useProfileContext } from './ProfileContext';

interface SettingsContextValue {
  settings: Settings;
  getSetting: <T = unknown>(path: SettingPath) => T;
  setSetting: <T = unknown>(path: SettingPath, value: T) => Promise<void>;
  updateSettings: (partial: Partial<Settings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, updateProfile } = useProfileContext();

  // Deep merge helper for Settings - handles up to 2 levels of nesting
  const deepMergeSettings = useCallback((target: Settings, source: Partial<Settings>): Settings => {
    const result: Record<string, unknown> = { ...target };

    Object.keys(source).forEach((key) => {
      const settingsKey = key as keyof Settings;
      const sourceValue = source[settingsKey];
      const targetValue = target[settingsKey];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        // Recursively merge nested objects
        const merged: Record<string, unknown> = { ...targetValue } as Record<string, unknown>;
        Object.keys(sourceValue).forEach((nestedKey) => {
          const nestedSourceValue = (sourceValue as Record<string, unknown>)[nestedKey];
          const nestedTargetValue = merged[nestedKey];

          if (
            nestedSourceValue &&
            typeof nestedSourceValue === 'object' &&
            !Array.isArray(nestedSourceValue) &&
            nestedTargetValue &&
            typeof nestedTargetValue === 'object' &&
            !Array.isArray(nestedTargetValue)
          ) {
            merged[nestedKey] = { ...nestedTargetValue, ...nestedSourceValue };
          } else if (nestedSourceValue !== undefined) {
            merged[nestedKey] = nestedSourceValue;
          }
        });
        result[settingsKey] = merged;
      } else if (sourceValue !== undefined) {
        result[settingsKey] = sourceValue;
      }
    });

    return result as unknown as Settings;
  }, []);

  // Merge profile settings with defaults
  const settings = useMemo((): Settings => {
    const profileSettings = (profile?.settings || {}) as Partial<Settings>;
    return deepMergeSettings(DEFAULT_SETTINGS, profileSettings);
  }, [profile?.settings, deepMergeSettings]);

  // Get a setting by dot-notation path
  const getSetting = useCallback(
    <T = unknown,>(path: SettingPath): T => {
      const parts = path.split('.');
      let value: unknown = settings;

      parts.forEach((part) => {
        value = (value as Record<string, unknown>)?.[part];
        if (value === undefined) {
          // Fallback to default if path doesn't exist
          let defaultValue: unknown = DEFAULT_SETTINGS;
          parts.forEach((p) => {
            defaultValue = (defaultValue as Record<string, unknown>)?.[p];
          });
          value = defaultValue;
        }
      });

      return value as T;
    },
    [settings],
  );

  // Set a single setting
  const setSetting = useCallback(
    async <T = unknown,>(path: SettingPath, value: T): Promise<void> => {
      if (!profile) throw new Error('No profile loaded');

      const parts = path.split('.');
      const newSettings = JSON.parse(JSON.stringify(settings)) as Settings; // Deep clone

      // Navigate to the parent object
      let current: Record<string, unknown> = newSettings as unknown as Record<string, unknown>;
      for (let i = 0; i < parts.length - 1; i += 1) {
        current = current[parts[i]] as Record<string, unknown>;
      }

      // Set the value
      current[parts[parts.length - 1]] = value;

      try {
        await updateProfile({
          ...profile,
          settings: newSettings,
        });
      } catch (error) {
        storageLogger.error('Failed to update setting:', path, value, error);
        throw error;
      }
    },
    [profile, settings, updateProfile],
  );

  // Update multiple settings at once
  const updateSettings = useCallback(
    async (partial: Partial<Settings>): Promise<void> => {
      if (!profile) throw new Error('No profile loaded');

      const newSettings = deepMergeSettings(settings, partial);

      await updateProfile({
        ...profile,
        settings: newSettings,
      });
    },
    [profile, settings, updateProfile, deepMergeSettings],
  );

  // Reset to defaults
  const resetSettings = useCallback(async (): Promise<void> => {
    if (!profile) throw new Error('No profile loaded');

    await updateProfile({
      ...profile,
      settings: DEFAULT_SETTINGS,
    });
  }, [profile, updateProfile]);

  const value = useMemo(
    (): SettingsContextValue => ({
      settings,
      getSetting,
      setSetting,
      updateSettings,
      resetSettings,
      isLoading: !profile,
    }),
    [settings, getSetting, setSetting, updateSettings, resetSettings, profile],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextValue => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

// Helper hook to get icon size for a specific group, reactive to settings changes
export const useIconSize = (group: keyof typeof ui.iconSizes): number => {
  const { settings } = useSettings();
  return ui.iconSizes[group][settings.ui.iconSize as IconSize];
};
